import { supabase } from '@/lib/supabase';

export const maskEmail = (email: string): string => {
  if (!email || email === 'unknown@example.com') return email;
  
  const [localPart, domain] = email.split('@');
  if (!localPart || !domain) return email;
  
  if (localPart.length <= 2) {
    return `${localPart[0]}*@${domain}`;
  }
  
  const maskedLocal = localPart[0] + '*'.repeat(localPart.length - 2) + localPart[localPart.length - 1];
  return `${maskedLocal}@${domain}`;
};

export const maskName = (name: string): string => {
  if (!name || name === 'Unknown User') return name;
  
  const words = name.trim().split(' ');
  
  return words.map(word => {
    if (word.length <= 1) return word;
    if (word.length <= 3) return word[0] + '*'.repeat(word.length - 1);
    return word[0] + '*'.repeat(word.length - 2) + word[word.length - 1];
  }).join(' ');
};

// Referral code utility functions
export const getReferralCodeFromUrl = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('ref') || urlParams.get('referral') || null;
};

export const storeReferralCode = (code: string): void => {
  // Store in cookie for 30 days
  const expirationDate = new Date();
  expirationDate.setTime(expirationDate.getTime() + (30 * 24 * 60 * 60 * 1000));
  document.cookie = `referralCode=${code}; expires=${expirationDate.toUTCString()}; path=/`;
  
  // Also store in localStorage as backup
  localStorage.setItem('referralCode', code);
};

export const getReferralCodeFromCookie = (): string | null => {
  const name = 'referralCode=';
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(';');
  
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return null;
};

export const resolveReferralCode = (): string | null => {
  // Check URL first, then cookie, then localStorage
  return getReferralCodeFromUrl() || 
         getReferralCodeFromCookie() || 
         localStorage.getItem('referralCode');
};

export const validateReferralCode = async (code: string): Promise<{
  isValid: boolean;
  affiliateId?: string;
  affiliateName?: string;
  error?: string;
}> => {
  try {
    if (!code || code.trim().length === 0) {
      return { isValid: false, error: 'Referral code is required' };
    }
    
    const { data: affiliate, error } = await supabase
      .from('affiliates')
      .select('id, name, status')
      .eq('referral_code', code.trim())
      .eq('status', 'active')
      .single();

    if (error) {
      console.error('Error validating referral code:', error);
      return { isValid: false, error: 'Failed to validate referral code' };
    }

    if (!affiliate) {
      return { isValid: false, error: 'Invalid or inactive referral code' };
    }

    return {
      isValid: true,
      affiliateId: affiliate.id,
      affiliateName: affiliate.name
    };
  } catch (error) {
    console.error('Exception validating referral code:', error);
    return { isValid: false, error: 'Failed to validate referral code' };
  }
};

export const clearReferralCode = (): void => {
  // Clear cookie
  document.cookie = 'referralCode=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  
  // Clear localStorage
  localStorage.removeItem('referralCode');
};
